import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import MaterialTable from '@material-table/core';
import flowRight from 'lodash.flowright';
import { Link } from 'react-router-dom';

import { getPartners } from './actions/getPartners';
import { actions as reduxActions } from './reducers';
import { mapTableOptions } from '../../consts/tableOptions';
import useTableLocalization from '../../utils/useTableLocalization';
import withDetailsView from '../../utils/withDetailsView';
import CustomTableTitle from '../../partials/CustomTableTitle';
import FilterButton from '../../partials/tableCustomButtons/FilterButton';
import tableColumns from './components/tableColumns';
import { doesArrayHasLength } from '../../utils/isDefined';
import useLocalTableStore from '../../utils/useLocalTableStore';
import customTableIcons from '../../partials/tableIcons/customTableIcons';
import filterCustomJobs from '../../utils/filterCustomJobs';
import CustomJobsMenu from '../../partials/customJobsMenu/CustomJobsMenu';
import RefreshButton from '../../partials/tableCustomButtons/RefreshButton';
import AddButton from '../../partials/tableCustomButtons/AddButton';
import withUserCheck from '../../utils/withUserCheck';
import { translate } from '../../i18n/I18nProvider';

function Partners({
  getPartners,
  partners,
  resetPartners,
  isLoading,
  columns,
  setColumns,
  customJobs,
  isSuperAdmin
}) {
  const [filtering, setFiltering] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { localization, language } = useTableLocalization();

  useEffect(() => {
    getPartners();
    return resetPartners;
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (refresh) {
      getPartners();
      setRefresh(false);
    }
    // eslint-disable-next-line
  }, [refresh]);

  const tableDefaultColumns = useMemo(() => {
    return tableColumns();
  }, []);

  const {
    pageSize,
    onChangeRowsPerPage,
    onChangeColumnHidden
  } = useLocalTableStore({
    tableName: 'Partners-1.1',
    setColumns,
    tableDefaultColumns,
    language
  });

  const mappedJobs = useMemo(() => {
    return filterCustomJobs(customJobs, 'Partners');
  }, [customJobs]);

  const actions = useMemo(
    () => {
      let buttons = [
        {
          icon: () => <FilterButton />,
          onClick: () => {
            setFiltering(!filtering);
          },
          isFreeAction: true,
          tooltip: translate('BUTTON.FILTER')
        }
      ];

      if (isSuperAdmin) {
        buttons = [
          ...buttons,
          {
            icon: () => (
              <Link
                to={{
                  pathname: '/admin/partners/settings',
                  state: { id: null, isNewPartner: true }
                }}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <AddButton buttonText="BUTTON.NEW_PARTNER" />
              </Link>
            ),
            onClick: () => {},
            isFreeAction: true
          }
        ];
      }

      if (doesArrayHasLength(mappedJobs)) {
        buttons = [
          ...buttons,
          {
            icon: () => <CustomJobsMenu customJobs={mappedJobs} />,
            onClick: () => {},
            isFreeAction: true
          }
        ];
      }

      buttons = [
        ...buttons,
        {
          icon: () => <RefreshButton tableHeader={true} />,
          onClick: () => setRefresh(true),
          isFreeAction: true
        }
      ];

      return buttons;
    },
    // eslint-disable-next-line
    [filtering, customJobs, isSuperAdmin, mappedJobs]
  );

  const options = useMemo(() => {
    return {
      ...mapTableOptions({ exportData: false }),
      selection: false,
      filtering,
      header: doesArrayHasLength(partners),
      pageSize
    };
  }, [filtering, partners, pageSize]);

  return (
    <MaterialTable
      title={
        <CustomTableTitle
          text="MENU.PARTNERS"
          icon={<i className="fas fa-handshake"></i>}
          isLoading={isLoading}
        />
      }
      icons={customTableIcons}
      actions={actions}
      columns={columns}
      data={partners}
      options={options}
      localization={localization}
      onChangeColumnHidden={onChangeColumnHidden}
      onRowsPerPageChange={onChangeRowsPerPage}
    />
  );
}

const mapStatesToProps = ({ partners, loader, auth }) => {
  return {
    ...partners,
    isLoading: loader.isSpinVisible,
    customJobs: auth.customJobs
  };
};

const mapDispatchToProps = {
  getPartners,
  resetPartners: reduxActions.resetPartners,
  setColumns: reduxActions.setColumns
};

export default flowRight(
  withDetailsView,
  withUserCheck,
  connect(mapStatesToProps, mapDispatchToProps)
)(Partners);
